import Cookies from 'js-cookie';
import { COOKIE_PREFERENCES_COOKIE } from './constants';
import fbPixelInit from '../lib/fbPixel';
import abConfig from '../abConfig';
import { updateGoogleTagConsent, loadGtmContainer } from './googleTag';
const ADVERTISING_CATEGORIES = [
  'Advertising',
  'Tag Managers',
  'CRM',
  'Customer Success',
  'Deep Linking',
  'Helpdesk',
  'Livechat',
  'Performance Monitoring',
  'Personalization',
  'SMS & Push Notifications',
  'Security & Fraud',
];

export function getAnonymousId() {
  const ANONYMOUS_ID_KEY = 'cookieBannerAnonymousId';
  let id = null;
  try {
    id = window.sessionStorage.getItem(ANONYMOUS_ID_KEY);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to get anonymous id from sessionStorage', e);
  }

  if (!id) {
    try {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(ANONYMOUS_ID_KEY, id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to set anonymous id in sessionStorage', e);
    }
  }

  return id;
}

export const getDestinationPreferences = (destinations, preferences) => {
  const destinationPreferences = {};

  Object.values(destinations).forEach(destination => {
    const category = destination.category;

    if (destinationPreferences[destination.id] !== false) {
      if (ADVERTISING_CATEGORIES.includes(category)) {
        destinationPreferences[destination.id] = preferences.advertising;
      }
    }

    // Fallback to marketing
    if (!(destination.id in destinationPreferences)) {
      destinationPreferences[destination.id] = preferences.marketingAndAnalytics;
    }
  });

  return destinationPreferences;
};

async function fetchSegmentDestinations(cdnHost, writeKey) {
  const res = await fetch(`${cdnHost}/v1/projects/${writeKey}/integrations`);

  if (!res.ok) {
    throw new Error(
      `Failed to fetch integrations for write key ${writeKey}: HTTP ${res.status} ${res.statusText}`
    );
  }

  const destinations = await res.json();

  for (const destination of destinations) {
    if (destination.name === 'Fullstory') {
      destination.id = destination.name;
    } else {
      destination.id = destination.creationName;
    }
    delete destination.creationName;
  }

  return destinations;
}

export function loadFbPixel(pixelId) {
  fbPixelInit();

  // eslint-disable-next-line
  fbq('init', pixelId || process.env.FB_PIXEL_ID);
}

export async function loadSegment(cookiePreferences) {
  const segmentCdnHost = process.env.SEGMENT_CDN_HOST;
  const segmentWriteKey = process.env.SEGMENT_KEY;
  const destinations = await fetchSegmentDestinations(segmentCdnHost, segmentWriteKey);
  const destinationPreferences = getDestinationPreferences(destinations, cookiePreferences);
  const allCookiesRejected = Object.values(destinationPreferences).every(value => !value);
  if (!allCookiesRejected) {
    analytics.load(segmentWriteKey, { integrations: destinationPreferences });
  }

  if (cookiePreferences.advertising) {
    loadFbPixel(process.env.FB_PIXEL_LP_ID);
  }

  updateGoogleTagConsent(cookiePreferences);

  // A third party GTM container id may be configured in Contentful (gtmContainerId).
  // Article routes wrap the entry in `content`, landing page routes spread the entry's
  // fields at the top level, so both places are checked.
  // Load it only when present for the current route and advertising consent is granted
  if (cookiePreferences.advertising && typeof window !== 'undefined') {
    const routeInfo = window.__routeInfo;
    const allProps = routeInfo && routeInfo.allProps ? routeInfo.allProps : {};
    const gtmContainerId =
      allProps.gtmContainerId || (allProps.content ? allProps.content.gtmContainerId : undefined);

    if (gtmContainerId) {
      loadGtmContainer(gtmContainerId);
    }
  }
}

export function initSegment() {
  function trackingStub(label, event, props) {
    const eventDescription = label === 'Page' ? `Viewed ${event} Page` : event;
    console.log(`Analytics ${label} Event:`, eventDescription, props); // eslint-disable-line no-console
  }

  if (typeof document !== 'undefined') {
    const routeInfo = window.__routeInfo;
    const allProps = routeInfo ? routeInfo.allProps : {};
    const pageName = allProps.name;
    if (process.env.SEGMENT_KEY) {
      const analytics = (window.analytics = window.analytics || []);

      // If the real analytics.js is already on the page return.
      if (analytics.initialize) return;

      // If the snippet was invoked already show an error.
      if (analytics.invoked) {
        // eslint-disable-next-line no-console
        if (window.console && console.error) {
          // eslint-disable-next-line no-console
          console.error('Segment snippet included twice.');
        }
        return;
      }

      // Invoked flag, to make sure the snippet
      // is never invoked twice.
      analytics.invoked = true;

      // A list of the methods in Analytics.js to stub.
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
      ];

      // Define a factory to create stubs. These are placeholders
      // for methods in Analytics.js so that you never have to wait
      // for it to load to actually record data. The `method` is
      // stored as the first argument, so we can replay the data.
      analytics.factory = function analyticsFactory(method) {
        return function methodStub() {
          const args = Array.prototype.slice.call(arguments);
          args.unshift(method);
          analytics.push(args);
          return analytics;
        };
      };

      // For each of our methods, generate a queueing stub.
      for (let i = 0; i < analytics.methods.length; i++) {
        const key = analytics.methods[i];
        analytics[key] = analytics.factory(key);
      }

      // Define a method to load Analytics.js from our CDN,
      // and that will be sure to only ever load it once.
      analytics.load = function analyticsLoad(key, options) {
        // Create an async script element based on your key.
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `${process.env.BASE_URL}/sp.js`;

        // Insert our script next to the first script element.
        const first = document.getElementsByTagName('script')[0];
        first.parentNode.insertBefore(script, first);
        analytics._loadOptions = options;
      };

      // Add a version to keep track of what's in the wild.
      analytics.SNIPPET_VERSION = '4.1.0';

      const trackingPreferences = Cookies.get(COOKIE_PREFERENCES_COOKIE);
      const isInApp = Cookies.get('ctc') === 'inapp-webview';

      if (trackingPreferences || isInApp) {
        loadSegment(
          trackingPreferences
            ? JSON.parse(trackingPreferences)
            : {
                necessary: true,
                marketingAndAnalytics: true,
                advertising: true,
              }
        );
      }
    } else {
      const analytics = {
        track: (event, props) => trackingStub('Track', event, props || null),
        page: (event, props) => trackingStub('Page', event, props || null),
      };
      window.analytics = analytics;
    }

    // BI requested to keep the old 'Viewed ${pageName} Page' event for a while
    analytics.page(pageName, allProps);
    // And  adding a new more general 'Viewed Landing Page' event
    analytics.page('Landing', allProps);

    const currentPath = allProps.pathWithoutBasename;
    const experiment = abConfig[currentPath] || abConfig['*'];
    if (experiment && allProps.variant) {
      analytics.track('Experiment Viewed', {
        experimentName: experiment.name,
        variantName: allProps.variant,
      });
    }
  }
}



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/analytics.js